// Pure helpers for the home hero showcase: a soft, endless crossfade of recent
// listings, each shown as it is today and then as it could be (photo → render
// of the same framing). No Vue, no Firebase — the Home view hands in the
// WebsiteAdV2 list it already reads.

// How many of the newest eligible ads the showcase draws from, and how many it
// shows per visit: a random pick from the latest ones, so the hero is never
// the same twice yet always shows fresh listings.
export const SHOWCASE_POOL = 12;
export const SHOWCASE_SIZE = 8;

const MARKET_SOLD = 'sold';

function euro(n) {
  return `${Math.round(n).toLocaleString('it-IT')} €`;
}

// Articles and prepositions that stay lowercase inside a place name.
const SMALL_WORDS = new Set(['di', 'del', 'dello', 'della', 'dei', 'degli', 'delle', 'da', 'a', 'al', 'alla', 'e', 'in', 'la', 'le', 'lo', 'il', 'sul', 'sulla']);

// "colle della maddalena" → "Colle della Maddalena",
// "mirafiori sud - onorato vigliani" → "Mirafiori Sud - Onorato Vigliani".
function titleCase(text) {
  return String(text || '')
    .trim()
    .toLowerCase()
    .replace(/(^|[\s'-])(\p{L}+)/gu, (m, sep, word, offset) =>
      sep + (offset > 0 && SMALL_WORDS.has(word) ? word : word[0].toUpperCase() + word.slice(1)));
}

// "Trilocale in Via Roma 1, Torino" → "Trilocale" (the street stays on the
// listing page; the hero shows typology + area only).
function typologyOf(ad) {
  const title = typeof ad.getTitle === 'function' ? ad.getTitle() : ad.title || '';
  return String(title).split(' in ')[0].trim();
}

// Lower bound of the "ristrutturazione completa" estimate, or null.
function worksFrom(ad) {
  const amounts = ad.renovationEstimate && ad.renovationEstimate.amounts;
  const completa = amounts && amounts.completa;
  const min = completa ? Number(completa.rangeMin) : NaN;
  return Number.isFinite(min) && min > 0 ? min : null;
}

// An ad can open the showcase when its card pairs a photograph with the render
// of the same room (WebsiteAdV2.imageRenders) and it is still on the market.
export function isShowcaseAd(ad) {
  return !!ad
    && Array.isArray(ad.images) && ad.images.length > 0
    && Array.isArray(ad.imageRenders) && !!ad.imageRenders[0]
    && ad.marketStatus !== MARKET_SOLD;
}

// One slide per ad, from the first photo/render pair of its card.
export function toShowcaseSlide(ad) {
  const zone = titleCase(ad.zone);
  const city = (ad.addressInfo && ad.addressInfo.city) || '';
  const price = typeof ad.getPrice === 'function' ? Number(ad.getPrice()) : Number(ad.price);
  const works = worksFrom(ad);
  return {
    id: ad.sourceId || ad.newarcHomeId,
    typology: typologyOf(ad),
    place: [zone, city].filter(Boolean).join(', '),
    price: Number.isFinite(price) && price > 0 ? euro(price) : '',
    works: works ? `Lavori stimati da ${euro(works)}` : '',
    photo: { src: ad.images[0], srcset: (ad.imageSrcsets && ad.imageSrcsets[0]) || '' },
    render: { src: ad.imageRenders[0], srcset: (ad.imageRenderSrcsets && ad.imageRenderSrcsets[0]) || '' },
  };
}

// Fisher–Yates on a copy; `random` is injectable for tests.
function shuffled(list, random) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// The showcase for this visit: the newest `pool` eligible ads (the list comes
// newest first), shuffled, `size` of them.
export function pickShowcaseSlides(ads, { pool = SHOWCASE_POOL, size = SHOWCASE_SIZE, random = Math.random } = {}) {
  const fresh = (ads || []).filter(isShowcaseAd).slice(0, pool);
  return shuffled(fresh, random).slice(0, size).map(toShowcaseSlide);
}
